/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';

import { CardListSkeleton, DataState, Text, Title } from '@/shared/ui';
import { Button } from '@/shared/ui/components/shadcn/button';
import { CreateVistoryModal } from './CreateVistoryModal';
import { VistoryStatusGroups } from './VistoryStatusGroups';
import { useVistoryData } from '../hooks/useVistoryData';
import { PropertiesService } from '@/services/requests/housien-services/PropertiesService';

export type LeaseVistoriesPanelProps = {
  agencyId: string;
  leaseId: string;
  /**
   * The lease's property. Required: the create route reads
   * body["property_id"], and the rooms of a new vistoria are derived
   * from this record — a derivation that never ran, because the panel
   * used to render the modal without handing it anything.
   */
  propertyId: string;
};

/**
 * Vistories for one lease: status-grouped list plus creation.
 *
 * Previously a tab on the legacy rent detail page; vistorias hang off the
 * Lease now [Fase 7, D6], so this is the panel's new host. The panel owns
 * its own data fetch rather than receiving it, which keeps LeaseDetailPage
 * from loading vistories for tabs the user never opens.
 *
 * @param props.agencyId The agency owning the lease.
 * @param props.leaseId The lease whose vistories to show.
 * @param props.propertyId The property the lease is for.
 * @returns The vistories panel.
 */
export function LeaseVistoriesPanel({
  agencyId, leaseId, propertyId,
}: LeaseVistoriesPanelProps) {
  const { t } = useTranslation('features/vistories');
  const { groups, counts, vistories, isLoading, isError, refetch } = useVistoryData(
    agencyId, leaseId,
  );
  const [showCreate, setShowCreate] = useState(false);
  // Fetched here rather than threaded down from LeaseDetailPage: the
  // rooms derivation is this feature's concern, and the panel already
  // owns its own data fetching.
  const property = useQuery({
    queryKey: ['leases', agencyId, 'property', propertyId],
    queryFn: () => PropertiesService.getProperty(agencyId, propertyId),
    enabled: Boolean(agencyId && propertyId),
  });

  return (
    <div className="flex flex-col gap-[var(--space-4)]">
      <div className="flex items-center justify-between">
        <Title order={2} size="lg" className="text-[var(--text-primary)]">
          {t('vistory.section_title')}
        </Title>
        <Button size="sm" onClick={() => setShowCreate(true)}>
          {t('vistory.create_button')}
        </Button>
      </div>
      <DataState
        isLoading={isLoading}
        isError={isError}
        isEmpty={vistories.length === 0}
        loadingFallback={<CardListSkeleton count={2} />}
        onRetry={refetch}
        emptyState={<VistoriesEmpty />}
      >
        <VistoryStatusGroups
          groups={groups}
          counts={counts}
          agencyId={agencyId}
          onUpdate={refetch}
        />
      </DataState>
      {showCreate && (
        <CreateVistoryModal
          agencyId={agencyId}
          leaseId={leaseId}
          propertyId={propertyId}
          property={property.data ?? null}
          onClose={() => setShowCreate(false)}
          onCreated={refetch}
        />
      )}
    </div>
  );
}

/**
 * Placeholder shown when a lease has no vistories yet.
 *
 * @returns The empty state.
 */
function VistoriesEmpty() {
  const { t } = useTranslation('features/vistories');
  return (
    <div className="flex flex-col items-center justify-center gap-[var(--space-2)] py-[var(--space-12)]">
      <Text size="sm" fw={500} className="text-[var(--text-primary)]">
        {t('vistory.empty_title')}
      </Text>
      <Text size="xs" className="text-[var(--text-secondary)]">
        {t('vistory.empty_description')}
      </Text>
    </div>
  );
}
